import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../supabase';
import type { CompanySettings, MenuItem, LeadsColumnConfig, LeadQuickInputFieldConfig } from '../types';
import { logger } from '../utils/logger';
import { dispatchTaskCompleted } from '../onboarding/dispatchTaskCompleted';
import { isCompanyProfileFilled } from '../onboarding/reconcileChecklist';

interface SettingsStore {
  settings: (CompanySettings & {
    menu_settings?: {
      items: MenuItem[];
      hidden_settings_tabs?: string[];
      menu_labels?: {
        clients_label?: string;
        clients_gender?: 'masculine' | 'feminine';
        clients_label_singular?: string;
        leads_label?: string;
        leads_gender?: 'masculine' | 'feminine';
        leads_label_singular?: string;
        use_quick_create_button?: boolean;
        use_quick_create_button_clients?: boolean;
        show_client_lead_status?: boolean;
      };
    };
    client_columns?: any[];
    leads_columns?: LeadsColumnConfig[];
    lead_quick_input_fields?: LeadQuickInputFieldConfig[];
    language?: 'en' | 'it';
  }) | null;
  loading: boolean;
  error: string | null;
  organizationId: string | null;
  initialized: boolean;
  retryCount?: number;
  setOrganizationId: (id: string) => void;
  fetchSettings: (forceRefresh?: boolean) => Promise<void>;
  updateSettings: (settings: Partial<CompanySettings & {
    menu_settings?: {
      items: MenuItem[];
      hidden_settings_tabs?: string[];
      menu_labels?: {
        clients_label?: string;
        clients_gender?: 'masculine' | 'feminine';
        clients_label_singular?: string;
        leads_label?: string;
        leads_gender?: 'masculine' | 'feminine';
        leads_label_singular?: string;
        use_quick_create_button?: boolean;
        use_quick_create_button_clients?: boolean;
        show_client_lead_status?: boolean;
      };
    };
    client_columns?: any[];
    leads_columns?: LeadsColumnConfig[];
    lead_quick_input_fields?: LeadQuickInputFieldConfig[];
    language?: 'en' | 'it';
    default_route?: string;
  }>) => Promise<void>;
  reset: () => void;
}

// Guard sincrono: il flag `loading` dello store viene settato solo dopo i
// primi await, quindi due chiamate concorrenti a fetchSettings partivano
// entrambe (doppia fetch profilo + settings al load della pagina).
let settingsFetchInFlight = false;

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set, get) => ({
      settings: null,
      loading: false,
      error: null,
      organizationId: null,
      initialized: false,
      retryCount: 0,
      
      setOrganizationId: (id: string) => {
        set({ organizationId: id });
      },

      fetchSettings: async (forceRefresh?: boolean) => {
        // If already loading, don't start another fetch
        if (get().loading) {
          console.log('[SettingsStore] Already loading, skipping fetch');
          return;
        }
        if (settingsFetchInFlight && !forceRefresh) {
          console.log('[SettingsStore] Fetch already in flight, skipping');
          return;
        }
        settingsFetchInFlight = true;

        try {
          const fetchId = `${Date.now().toString(36)}`;
          logger.log('settings', `Caricamento settings [${fetchId}]`);
          
          // Get current user session to fetch the organization_id
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error(`[SettingsStore] [${fetchId}] Auth session error:`, sessionError);
            throw new Error(`Auth session error: ${sessionError.message}`);
          }
          
          if (!session) {
            console.log(`[SettingsStore] [${fetchId}] No active session`);
            set({ 
              loading: false, 
              initialized: true,
              settings: null,
              error: null 
            });
            return;
          }
          
          // Get user profile with organization ID
          const { data: userProfile, error: profileError } = await supabase
            .from('user_profiles')
            .select('organization_id, is_super_admin')
            .eq('id', session.user.id)
            .single();
            
          if (profileError) {
            console.error(`[SettingsStore] [${fetchId}] Profile error:`, profileError);
            throw new Error(`Profile error: ${profileError.message}`);
          }
          
          // Logged user profile
          
          // Se abbiamo già le impostazioni, verifichiamo che corrispondano all'organizzazione corrente
          if (!forceRefresh && get().settings && get().organizationId) {
            // Check if the stored organization ID matches the current user's organization
            if (get().organizationId === userProfile?.organization_id) {
              // Settings already loaded for this organization
              set({ initialized: true });
              return;
            } else {
              console.log(`[SettingsStore] [${fetchId}] Organization changed from ${get().organizationId} to ${userProfile?.organization_id}, reloading settings`);
              // Clear settings and reload for the new organization
              set({ 
                settings: null, 
                organizationId: null,
                initialized: false
              });
            }
          } else if (forceRefresh) {
            console.log(`[SettingsStore] [${fetchId}] Force refresh enabled, always fetch fresh settings`);
          }

        set({ loading: true, error: null });
        
        // Performance logger
        const startTime = performance.now();
          
        // At this point we already have the userProfile from the check above

          // Special handling for super admin SENZA organization_id:
          // mostra lo stub "Admin Panel" col menu Organizations.
          // Se il superadmin ha un'organization_id, carica SEMPRE le settings
          // reali della sua org — altrimenti il pannello Company, le condizioni
          // del PDF preventivo, logo, firma legale e tutti i dati fiscali
          // risultano vuoti (bug evidente in CompanySettings + pdf.ts).
          // Il menu del superadmin (Organizations, Feature Toggles, etc.) viene
          // mostrato comunque via flag isSuperAdmin da useAuth, non dipende da
          // questo shortcut.
          if (userProfile?.is_super_admin && !userProfile?.organization_id) {
            set({
              settings: {
                business_name: 'Admin Panel',
                menu_settings: {
                  items: [
                    {
                      id: 'organizations',
                      label: 'Organizations',
                      icon: 'Building2',
                      to: '/organizations',
                      order: 0,
                      resource: 'users',
                      action: 'manage'
                    }
                  ],
                  hidden_settings_tabs: []
                }
              },
              loading: false,
              initialized: true,
              organizationId: null
            });

            const endTime = performance.now();
            console.log(`[SettingsStore] Settings loaded for super admin in ${(endTime - startTime).toFixed(1)}ms`);
            return;
          }

          if (!userProfile?.organization_id) {
            // Partner admin senza org: skip fetch e marca come inizializzato
            if (userProfile?.is_partner_admin) {
              set({
                loading: false,
                initialized: true,
                organizationId: null,
                error: null
              });
              const endTime = performance.now();
              console.log(`[SettingsStore] Partner admin detected, no settings needed (${(endTime - startTime).toFixed(1)}ms)`);
              return;
            }
            throw new Error('User not associated with an organization');
          }

          // Get organization settings - get the most recent one
          // Add a timestamp param to avoid query caching
          const timestamp = Date.now();
          console.log(`[SettingsStore] [${fetchId}] Fetching settings for organization ${userProfile.organization_id} (timestamp: ${timestamp})`);
          
          const { data: settingsData, error: settingsError } = await supabase
            .from('settings')
            .select('*')
            .eq('organization_id', userProfile.organization_id)
            .order('updated_at', { ascending: false })
            .limit(1);

          if (settingsError) {
            console.error(`[SettingsStore] [${fetchId}] Error fetching settings:`, settingsError);
            throw settingsError;
          }
          
          // Log the raw data for debugging
          console.log(`[SettingsStore] [${fetchId}] Settings data fetched:`, settingsData);
          
          // Use the latest settings record
          const settings = settingsData && settingsData.length > 0 ? settingsData[0] : null;
          
          // Log the specific fields we care about for quote numbers
          if (settings) {
            console.log(`[SettingsStore] [${fetchId}] Quote number fields:`, {
              last_quote_number: settings.last_quote_number,
              last_quote_year: settings.last_quote_year,
              quote_number_start: settings.quote_number_start
            });
          }

          // If no settings exist, create default settings
          if (!settings) {
            const defaultSettings = {
              business_name: 'Your Company Name',
              menu_settings: {
                items: [
                  { 
                    id: 'dashboard', 
                    label: 'Dashboard', 
                    icon: 'LayoutDashboard', 
                    to: '/dashboard', 
                    order: 0,
                    resource: 'dashboard',
                    action: 'view'
                  },
                  { 
                    id: 'clients', 
                    label: 'Clienti', 
                    icon: 'Users', 
                    to: '/clients', 
                    order: 1,
                    resource: 'clients',
                    action: 'view'
                  },
                  { 
                    id: 'deals', 
                    label: 'Trattative', 
                    icon: 'FileText', 
                    to: '/deals', 
                    order: 2,
                    resource: 'deals',
                    action: 'view'
                  },
                  { 
                    id: 'referrers', 
                    label: 'Segnalatori', 
                    icon: 'UserCheck', 
                    to: '/referrers', 
                    order: 3,
                    resource: 'referrers',
                    action: 'view'
                  },
                  { 
                    id: 'partners', 
                    label: 'Partner Esterni', 
                    icon: 'Handshake', 
                    to: '/partners', 
                    order: 4,
                    resource: 'external_partners',
                    action: 'view'
                  },
                  { 
                    id: 'services', 
                    label: 'Servizi', 
                    icon: 'Package', 
                    to: '/services', 
                    order: 5,
                    resource: 'services',
                    action: 'view'
                  },
                  { 
                    id: 'settings', 
                    label: 'Impostazioni', 
                    icon: 'Settings', 
                    to: '/settings', 
                    order: 99,
                    resource: 'settings',
                    action: 'view'
                  }
                ],
                hidden_settings_tabs: []
              },
              default_route: '/dashboard',
              organization_id: userProfile.organization_id,
              language: 'it', // Default prodotto: italiano (il DEFAULT colonna è 'en' → UI mista)
              whatsapp_global_config: true, // Default to global WhatsApp configuration
              commission_enabled: false, // Default commission settings
              default_commission_rate: 0
            };

            const { data: newSettings, error: createError } = await supabase
              .from('settings')
              .insert(defaultSettings)
              .select()
              .single();

            if (createError) throw createError;

            set({
              settings: newSettings,
              loading: false,
              initialized: true,
              organizationId: userProfile.organization_id,
              retryCount: 0 // Reset retry count on success
            });
          } else {
            set({
              settings,
              loading: false,
              initialized: true,
              organizationId: userProfile.organization_id,
              retryCount: 0 // Reset retry count on success
            });
          }
          
          const endTime = performance.now();
          console.log(`[SettingsStore] Settings loaded in ${(endTime - startTime).toFixed(1)}ms`);
        } catch (error) {
          console.error('[SettingsStore] Error:', error);
          
          // Attempt automatic retry after a short delay
          const currentRetry = get().retryCount || 0;
          if (currentRetry < 2) {
            console.log(`[SettingsStore] Auto-retry attempt ${currentRetry + 1}/2`);
            set({ retryCount: currentRetry + 1 });
            
            setTimeout(() => {
              get().fetchSettings();
            }, 500); // Quick retry after 500ms
            
            // Keep loading state during retry
            set({
              loading: true,
              initialized: false,
              error: null
            });
          } else {
            // Only show error after retries are exhausted
            set({
              error: friendlyDbError(error) || 'Unknown error',
              loading: false,
              initialized: true,
              settings: null,
              retryCount: 0 // Reset for next time
            });
          }
        } finally {
          settingsFetchInFlight = false;
        }
      },

      updateSettings: async (settings) => {
        const organizationId = get().organizationId;
        if (!organizationId) {
          throw new Error('No organization ID available');
        }

        try {
          // Generate a unique ID for this update operation
          const updateId = `settings_update_${Date.now().toString(36)}`;
          console.log(`[SettingsStore] [${updateId}] Starting settings update...`);
          
          // Ottieni le impostazioni attuali prima dell'aggiornamento
          const currentSettings = get().settings;
          
          // Prepara l'oggetto di aggiornamento
          const updateData = {
            ...settings,
            updated_at: new Date().toISOString()
          };
          
          // Log all quote number fields specifically if they're being updated
          if ('last_quote_number' in settings || 'last_quote_year' in settings || 'quote_number_start' in settings) {
            console.log(`[SettingsStore] [${updateId}] ⚠️ AGGIORNAMENTO NUMERO PREVENTIVO:`, {
              old_last_quote_number: currentSettings?.last_quote_number,
              new_last_quote_number: settings.last_quote_number,
              old_last_quote_year: currentSettings?.last_quote_year,
              new_last_quote_year: settings.last_quote_year,
              old_quote_number_start: currentSettings?.quote_number_start,
              new_quote_number_start: settings.quote_number_start
            });
          }
          
          console.log(`[SettingsStore] [${updateId}] Aggiornamento impostazioni con dati:`, updateData);
          
          // Se esistono già impostazioni, modifica sempre la riga esistente anziché crearne una nuova
          if (currentSettings) {
            console.log(`[SettingsStore] [${updateId}] Aggiornamento impostazioni esistenti`);
            
            // Primo recuperiamo l'ID della settings più recente
            // Evitiamo la cache aggiungendo un timestamp
            const timestamp = Date.now();
            
            const { data: settingsData, error: fetchError } = await supabase
              .from('settings')
              .select('id, business_name, last_quote_number, last_quote_year, quote_number_start')
              .eq('organization_id', organizationId)
              .order('updated_at', { ascending: false })
              .limit(1);
              
            if (fetchError) {
              console.error(`[SettingsStore] [${updateId}] Errore nel recupero delle impostazioni:`, fetchError);
              throw fetchError;
            }
            
            if (!settingsData || settingsData.length === 0) {
              console.error(`[SettingsStore] [${updateId}] Nessuna impostazione trovata per l'organizzazione:`, organizationId);
              throw new Error('No settings found for this organization');
            }
            
            const settingsId = settingsData[0].id;
            console.log(`[SettingsStore] [${updateId}] Impostazioni recuperate per aggiornamento:`, settingsData[0]);
            
            // Assicuriamoci che il business_name sia preservato
            if (!updateData.business_name && settingsData[0].business_name) {
              updateData.business_name = settingsData[0].business_name;
            }
            
            // Ora aggiorniamo la riga specifica utilizzando l'ID
            const { data: updatedRow, error } = await supabase
              .from('settings')
              .update(updateData)
              .eq('id', settingsId)
              .select();
              
            if (error) {
              console.error(`[SettingsStore] [${updateId}] Errore nell'aggiornamento:`, error);
              throw error;
            }

            // La RLS su settings (dal 31/07/2026) FILTRA la riga invece di negare quando
            // manca il permesso settings:update: PostgREST risponde 200 con zero righe e
            // nessun errore. Senza questo controllo il salvataggio falliva in silenzio e
            // la scheda si ricaricava con i valori vecchi, senza spiegazioni.
            if (!updatedRow || updatedRow.length === 0) {
              console.error(
                `[SettingsStore] [${updateId}] Nessuna riga aggiornata: permesso di modifica delle impostazioni mancante.`,
              );
              throw new Error(
                'Non hai i permessi per modificare le impostazioni dell\'organizzazione. Chiedi a un amministratore.',
              );
            }

            console.log(`[SettingsStore] [${updateId}] Impostazioni aggiornate con successo:`, updatedRow);
          } else {
            // Solo se non esistono impostazioni, crea una nuova riga
            console.log(`[SettingsStore] [${updateId}] Creazione nuove impostazioni`);
            
            // Assicurati che business_name sia incluso (è obbligatorio)
            if (!updateData.business_name) {
              updateData.business_name = 'CRM';
            }
            
            // Aggiungi sempre l'organization_id
            updateData.organization_id = organizationId;
            
            const { data: insertedRow, error } = await supabase
              .from('settings')
              .insert(updateData)
              .select();
              
            if (error) {
              console.error(`[SettingsStore] [${updateId}] Errore nell'inserimento:`, error);
              throw error;
            }
            
            console.log(`[SettingsStore] [${updateId}] Nuove impostazioni create con successo:`, insertedRow);
          }

          // Refresh settings - add a timestamp to avoid caching issues
          const timestamp = Date.now();
          console.log(`[SettingsStore] [${updateId}] Ricaricamento impostazioni dopo l'aggiornamento (timestamp: ${timestamp})`);
          
          const { data: settingsData, error: fetchError } = await supabase
            .from('settings')
            .select('*')
            .eq('organization_id', organizationId)
            .order('updated_at', { ascending: false })
            .limit(1);

          if (fetchError) {
            console.error(`[SettingsStore] [${updateId}] Errore nel ricaricamento delle impostazioni:`, fetchError);
            throw fetchError;
          }
          
          // Use the latest settings
          const updatedSettings = settingsData && settingsData.length > 0 ? settingsData[0] : null;
          if (!updatedSettings) {
            console.error(`[SettingsStore] [${updateId}] Impossibile recuperare le impostazioni aggiornate`);
            throw new Error('Failed to fetch updated settings');
          }
          
          console.log(`[SettingsStore] [${updateId}] Impostazioni ricaricate con successo:`, {
            last_quote_number: updatedSettings.last_quote_number,
            last_quote_year: updatedSettings.last_quote_year,
            quote_number_start: updatedSettings.quote_number_start
          });
          
          set({ settings: updatedSettings });
          console.log(`[SettingsStore] [${updateId}] Stato dello store aggiornato con nuove impostazioni`);

          // Onboarding: il profilo azienda conta come completato solo con
          // P.IVA o sede (business_name/phone/email sono seedati in registrazione)
          if (isCompanyProfileFilled(updatedSettings)) {
            dispatchTaskCompleted('profile', 'settings_store_update');
          }
        } catch (error) {
          console.error('[SettingsStore] Update error:', error);
          throw error;
        }
      },

      reset: () => {
        // Per prevenire cicli di re-render, controlliamo se c'è qualcosa da resettare
        const state = get();
        if (!state.settings && !state.organizationId && !state.initialized) {
          console.log('[SettingsStore] Skipping reset, already in initial state');
          return;
        }
        
        console.log('[SettingsStore] Resetting to initial state');
        set({
          settings: null,
          loading: false,
          error: null,
          organizationId: null,
          initialized: false
        });
      }
    }),
    {
      name: 'settings-storage',
      partialize: (state) => ({
        settings: state.settings,
        organizationId: state.organizationId,
        initialized: state.initialized
      })
    }
  )
);